import { catmatAcitretina, catmatIsotretinoina, catmatTretinoina } from 'api/ImpressaoMedicamentos'
import { parse } from 'components/form'
import { addDays, addHours, addMonths, addWeeks, isAfter, parseISO, subDays, subHours } from 'date-fns'
import {
  DisponibilidadeHorusEnum,
  ReceitaMedicamento,
  TipoFrequencia,
  UnidadeMedidaTempoEnum,
} from 'graphql/types.generated'
import { msg, parseNumber } from 'util/validation'
import { v4 as uuidv4 } from 'uuid'

import { PrescricaoDigitalFormModel } from '../modal/prescricao-digital/model-prescricaoDigital'
import { CATMAT_INSULINA_NPH } from '../modal/utils/constants-prescricao'
import {
  CreateMedicamentoOptions,
  GrupoReceitaMedicamento,
  MedicamentoFormModel,
  TipoDoseEnum,
  TipoMotivoPrescricaoEnum,
} from '../model-prescricao'

export const calculateDataFimTratamento = (amount: number, date: LocalDate, scale: UnidadeMedidaTempoEnum) => {
  if (!amount || !date) {
    return null
  }

  if (scale) {
    switch (scale) {
      case UnidadeMedidaTempoEnum.DIAS:
        return addDays(parseISO(date), amount - 1)
      case UnidadeMedidaTempoEnum.SEMANAS:
        return subDays(addWeeks(parseISO(date), amount), 1)
      case UnidadeMedidaTempoEnum.MESES:
        return subDays(addMonths(parseISO(date), amount), 1)
      default:
        return null
    }
  } else {
    return subHours(addHours(parseISO(date), amount), 24)
  }
}

export function isCampoInvalido(condition: boolean) {
  return (value: string | number) => (condition ? msg('invalid', value) : null)
}

export function duracaoFrequencia(
  frequencia: number,
  dataInicioTratamento: LocalDate,
  escalaFrequenciaDose: UnidadeMedidaTempoEnum,
  escalaDuracao: UnidadeMedidaTempoEnum
) {
  return (value: number) => {
    const dataFinalFrequencia = calculateDataFimTratamento(frequencia, dataInicioTratamento, escalaFrequenciaDose)
    const dataFinalDuracao = calculateDataFimTratamento(parseNumber(value), dataInicioTratamento, escalaDuracao)

    return isAfter(dataFinalFrequencia, dataFinalDuracao)
      ? 'Duração do tratamento deve ser maior ou igual à frequência da dose'
      : null
  }
}

export const calculaDisponibilidadeHorus = (
  quantidadeDisponivel: number,
  quantidadeDesejada: number
): DisponibilidadeHorusEnum => {
  if (quantidadeDisponivel === 0) return DisponibilidadeHorusEnum.INDISPONIVEL
  else if (quantidadeDesejada > quantidadeDisponivel) return DisponibilidadeHorusEnum.PARCIALMENTE_DISPONIVEL
  else return DisponibilidadeHorusEnum.DISPONIVEL
}

export const hasIsotretinoinaTretinoinaAcitretina = (medicamentosToPrint: MedicamentoFormModel[]) =>
  medicamentosToPrint.some((med) => {
    return (
      catmatAcitretina.some((element) => {
        return element === med.principioAtivoCombo?.codigoCatMat
      }) ||
      catmatIsotretinoina.some((element) => {
        return element === med.principioAtivoCombo?.codigoCatMat
      }) ||
      catmatTretinoina.some((element) => {
        return element === med.principioAtivoCombo?.codigoCatMat
      })
    )
  })

export const isInsulinaNph = (codigoCatMat: string | null | undefined) =>
  !!codigoCatMat && CATMAT_INSULINA_NPH.includes(codigoCatMat)

export const isOnPrescricaoDigitalAtiva = (
  prescricaoDigital: PrescricaoDigitalFormModel,
  medicamento: MedicamentoFormModel
) =>
  !!medicamento.codigoPrescricaoDigital &&
  !prescricaoDigital?.[medicamento.codigoPrescricaoDigital]?.prescricaoInvalidada

export const hasPrescricaoDigitalAtiva = (prescricaoDigital?: PrescricaoDigitalFormModel | null): boolean => {
  if (!prescricaoDigital) return false
  return Object.values(prescricaoDigital).some((el) => !el.prescricaoInvalidada)
}
export const createDefaultMedicamento = (
  dataAtendimento: Date,
  options: CreateMedicamentoOptions = {}
): MedicamentoFormModel => {
  const { isMotivoPrescricaoEnabled, overrides } = options

  const baseMedicamento: MedicamentoFormModel = {
    id: uuidv4(),
    dataInicioTratamento: parse(dataAtendimento),
    escalaDuracao: UnidadeMedidaTempoEnum.DIAS,
    tipoFrequencia: TipoFrequencia.INTERVALO,
    tipoDose: TipoDoseEnum.COMUM,
  }

  if (isMotivoPrescricaoEnabled) {
    baseMedicamento.tipoMotivoPrescricao = TipoMotivoPrescricaoEnum.CID10
  }

  return {
    ...baseMedicamento,
    ...overrides,
  }
}

export const groupAndSortReceitaMedicamentoByReceitaUsoContinuoGrupoId = (
  medicamentos?: ReceitaMedicamento[],
  skipGroupSort: boolean = false
): GrupoReceitaMedicamento[] => {
  if (!medicamentos || medicamentos.isEmpty()) {
    return []
  }

  const ordemChaves = []

  const grupos = medicamentos.reduce((acc: Record<string, ReceitaMedicamento[]>, receita: ReceitaMedicamento) => {
    const chaveGrupo = receita.receitaUsoContinuoGrupo?.id ?? receita.id
    if (!acc[chaveGrupo]) {
      acc[chaveGrupo] = []
      ordemChaves.push(chaveGrupo)
    }
    acc[chaveGrupo].push(receita)
    return acc
  }, {})

  const listaGrupos = ordemChaves.map((chave) => grupos[chave])

  const gruposComItensOrdenados = listaGrupos.map((grupo) =>
    [...grupo].sort((a, b) => b.atendProf?.iniciadoEm - a.atendProf?.iniciadoEm)
  )

  const gruposOrdenados = skipGroupSort
    ? gruposComItensOrdenados
    : [...gruposComItensOrdenados].sort((grupoA, grupoB) => {
        const dataA = grupoA.first().atendProf?.iniciadoEm
        const dataB = grupoB.first().atendProf?.iniciadoEm
        return dataB - dataA
      })

  return gruposOrdenados.map((grupo) => ({ prescricoes: grupo }))
}

export const getMedicamentosAgrupados = (medicamentos?: ReceitaMedicamento[]): ReceitaMedicamento[] => {
  const grupos = groupAndSortReceitaMedicamentoByReceitaUsoContinuoGrupoId(medicamentos, true)

  return grupos.map(({ prescricoes }) => {
    const prescricaoMaisRecente = prescricoes.first()
    return {
      ...prescricaoMaisRecente,
      dtInicioTratamento:
        prescricaoMaisRecente.receitaUsoContinuoGrupo?.dtInicioTratamento ?? prescricaoMaisRecente.dtInicioTratamento,
    }
  })
}
